import { Injectable } from '@angular/core';
import { ApiClass } from '@data/class/api-class';
import { environment } from '@env/environment';
import { Observable, catchError, map, of } from 'rxjs';
import { IDrippingListResponse } from '../../interfaces/api-response.interface';
import { IResponseArray } from '@data/interfaces/iresponseArray.interface';
import { IDaysRange } from '../../interfaces/channel-config.interface';
import { DAYS_RANGE_LABELS } from '../../constants/strategies.constants';

/**
 * Servicio API para gestión de estrategias del módulo Dripping
 * ACTUALIZADO: Usa daysRange en lugar de daysOffset
 */
@Injectable({
  providedIn: 'root'
})
export class ApiStrategiesService extends ApiClass {

  constructor() {
    super();
    this.path = 'dripping/strategies';
  }

  getAll(page = 1, limit = 10, filters = {}): Observable<IDrippingListResponse> {
    const response: IDrippingListResponse = { 
      error: true, 
      msg: '', 
      data: {
        data: [],
        total: 0
      },
      status: 200,
      page: 1, 
      itemsByPage: 10, 
      totalItems: 0 
    };

    return this.http.post<IResponseArray>(environment.apiUrl + this.path, { page, limit, filters }).pipe(
      map((r) => {
        const result = r as IDrippingListResponse;
        // Transformar datos para agregar campos calculados
        if (result.data?.data) {
          result.data.data = result.data.data.map((item: any) => ({
            ...item,
            activeStatus: item.active ? 'true' : 'false',
            autoExecuteStatus: item.autoExecute ? 'true' : 'false',
            scenarioDisplay: this.getScenarioDisplayText(item.scenario, item.daysRange),
            daysRangeDisplay: this.getDaysRangeDisplay(item.scenario, item.daysRange),
            activeChannels: this.getActiveChannelsList(item.channels),
            frequencyInfo: this.getFrequencyInfo(item.channels)
          }));
        }
        return result;
      }),
      catchError((e) => of(response))
    );
  }

  /**
   * Obtiene el texto descriptivo del escenario con rango de días
   */
  private getScenarioDisplayText(scenario: string, daysRange?: IDaysRange): string {
    const labels = DAYS_RANGE_LABELS[scenario as keyof typeof DAYS_RANGE_LABELS];
    
    if (!labels) return scenario;
    
    if (scenario === 'DUE_DATE') {
      return 'Día del Vencimiento';
    }
    
    if (daysRange) {
      return labels.preview(daysRange.minDays, daysRange.maxDays);
    }
    
    return labels.title;
  }

  /**
   * Obtiene display formateado del rango de días
   */
  private getDaysRangeDisplay(scenario: string, daysRange?: IDaysRange): string {
    if (!daysRange) return '-';
    
    if (scenario === 'DUE_DATE') {
      return 'N/A';
    }
    
    const { minDays, maxDays } = daysRange;
    
    if (maxDays === null) {
      return `${minDays}+ días`;
    }
    
    if (minDays === maxDays) {
      return `${minDays} días`;
    }
    
    return `${minDays} - ${maxDays} días`;
  }

  /**
   * Obtiene información de frecuencia de los canales
   */
  private getFrequencyInfo(channels: any): string {
    if (!channels) return '';
    
    const frequencyParts: string[] = [];
    
    Object.keys(channels).forEach(key => {
      const channel = channels[key];
      if (channel?.enabled && channel.frequency > 1) {
        const channelName = key.charAt(0).toUpperCase() + key.slice(1);
        frequencyParts.push(`${channelName}: ${channel.frequency}x`);
      }
    });
    
    return frequencyParts.length > 0 ? frequencyParts.join(', ') : 'Normal';
  }

  /**
   * Obtiene la lista de canales activos formateada
   */
  private getActiveChannelsList(channels: any): string {
    if (!channels) return '';
    
    const activeChannels = Object.keys(channels)
      .filter(key => channels[key]?.enabled)
      .map(key => {
        // Capitalizar primera letra
        return key.charAt(0).toUpperCase() + key.slice(1);
      });
    
    return activeChannels.join(', ') || 'Ninguno';
  }

  /**
   * Activar estrategia
   */
  activate(id: string): Observable<any> {
    return this.postCollection({}, `${id}/activate`);
  }

  /**
   * Desactivar estrategia
   */
  deactivate(id: string): Observable<any> {
    return this.postCollection({}, `${id}/deactivate`);
  }

  /**
   * Obtener leases elegibles para la estrategia
   * Endpoint: GET /dripping/strategies/:id/eligible-leases
   */
  getEligibleLeases(id: string): Observable<any> {
    const url = `${environment.apiUrl}${this.path}/${id}/eligible-leases`;
    
    return this.http.get<any>(url).pipe(
      map((r) => {
        // El backend puede retornar:
        // 1. Array directo: [lease1, lease2, ...]
        // 2. Objeto con data array: { data: [lease1, lease2, ...] }
        // 3. Objeto con estructura paginada: { data: { total: X, leases: [...] } }
        let leasesData = [];
        
        if (Array.isArray(r)) {
          leasesData = r;
        } else if (r.data) {
          if (Array.isArray(r.data)) {
            leasesData = r.data;
          } else if (r.data.leases && Array.isArray(r.data.leases)) {
            // Estructura: { data: { total: X, leases: [...] } }
            leasesData = r.data.leases;
          }
        }
        
        return { 
          error: false, 
          msg: r.msg || 'Leases elegibles obtenidos exitosamente', 
          data: leasesData,
          total: r.data?.total || leasesData.length
        };
      }),
      catchError((e) => {
        console.error('❌ Error al obtener leases elegibles:', e);
        
        // Mensaje específico para 404
        let errorMsg = 'Error al obtener leases elegibles';
        if (e.status === 404) {
          errorMsg = 'El endpoint de leases elegibles no está disponible. Contacte al administrador del sistema.';
        } else if (e.status === 403) {
          errorMsg = 'No tiene permisos para ver los leases elegibles de esta estrategia.';
        } else if (e.error?.msg) {
          errorMsg = e.error.msg;
        }
        
        return of({ 
          error: true, 
          msg: errorMsg, 
          data: null,
          status: e.status
        });
      })
    );
  }
}
